import { useMemo, useRef, useState } from "react";

import merge from "lodash/merge";

import { Control, Controller, FieldValues } from "react-hook-form";

import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import { AutocompleteProps, Theme } from "@mui/material";
import { InputBaseComponentProps } from "@mui/material/InputBase/InputBase";
import { useTheme } from "@mui/material/styles";
import { SxProps, useMediaQuery } from "@mui/system";

import { Tooltip } from "../../content";
import { getInputLabelHeight } from "@covertree/utils";
import { InputLabelHeight } from "@covertree/types";

export interface SelectOption<T> {
  label: string;
  value: T;
  icon?: string;
}

export interface TestableListboxProps
  extends React.HTMLAttributes<HTMLUListElement> {
  "data-testid"?: string;
}

type BaseProps<TValues extends FieldValues, TOptionType> = {
  id: string;
  name: string;
  control: Control<TValues>;
  options: SelectOption<TOptionType>[];
  label: React.ReactNode;
  labelHeight?: InputLabelHeight;
  placeholder?: string;
  dataTestId?: string;
  tooltip?: string;
  inputStyle?: SxProps;
  inputProps?: InputBaseComponentProps;
  optional?: boolean;
  disabled?: boolean;
  fontSize?: number;
  labelFontSize?: number;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlurChange?: React.FocusEventHandler<HTMLInputElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
};

export type FormAutocompleteProps<
  TValues extends FieldValues,
  TOptionType,
> = Omit<
  AutocompleteProps<SelectOption<TOptionType>, false, false, false>,
  keyof BaseProps<TValues, TOptionType> | "renderInput"
> &
  BaseProps<TValues, TOptionType>;

export const FormAutocomplete = <TValues extends FieldValues, TOptionType>({
  id,
  name,
  control,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  options,
  placeholder,
  dataTestId,
  tooltip,
  inputStyle = {},
  inputProps: inputPropsFromProps = {},
  optional = false,
  disabled = false,
  fontSize,
  labelFontSize,
  disablePortal = true,
  onBlurChange,
  onInputChange,
  defaultValue = "",
  ...autocompleteProps
}: FormAutocompleteProps<TValues, TOptionType>): JSX.Element => {
  const theme = useTheme();
  const fontSizeValue = fontSize ? `${fontSize}px` : undefined;

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const [dropdownWidth, setDropdownWidth] = useState(0);
  const labelRef = useRef<HTMLParagraphElement>(null);

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      defaultValue={defaultValue as any}
      control={control}
      render={({ field: { value, onBlur, onChange, ref }, fieldState }) => (
        <FormControl fullWidth error={fieldState.invalid}>
          {(label || tooltip) && (
            <InputLabel
              htmlFor={id}
              id={`${id}-label`}
              variant="standard"
              sx={{
                height: isDesktop ? labelComponentHeight : "auto",
                display: "flex",
                alignItems: "flex-end",
                position: "static",
                transform: "none",
                "& > p": {
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  width: "100%",
                  whiteSpace: "break-spaces",
                },
              }}
              shrink>
              <Typography
                variant="body2"
                ref={labelRef}
                fontSize={labelFontSize ?? fontSize}>
                {label}
                {tooltip && (
                  <Tooltip
                    dataTestId={`${dataTestId}-tooltip`}
                    tooltip={tooltip ?? ""}
                    size="large"
                    fontSize={labelFontSize ?? fontSize}
                    sx={{ marginLeft: theme.spacing() }}
                    placement="bottom"
                  />
                )}
              </Typography>
            </InputLabel>
          )}
          <Autocomplete
            disableClearable
            forcePopupIcon={false}
            disablePortal={disablePortal}
            componentsProps={{
              popper: {
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                  {
                    name: "preventOverflow",
                    enabled: false,
                  },
                ],
                sx: {
                  maxHeight: 150,
                  width: dropdownWidth,
                  ...(fontSize && {
                    "& .MuiAutocomplete-option": {
                      fontSize: `${fontSize}px`,
                    },
                  }),
                },
              },
            }}
            id={id}
            ref={ref}
            // MUI warns about a value it has no option for, but skips the
            // check on null, so an unpicked field is null rather than empty.
            value={(value?.value ? value : null) as never}
            options={options}
            ListboxProps={
              {
                "data-testid": "autocomplete-input-listbox",
              } as TestableListboxProps
            }
            getOptionLabel={(option) => option.label ?? ""}
            isOptionEqualToValue={(
              opt: SelectOption<TOptionType>,
              val: SelectOption<TOptionType>
            ) => opt.value === val.value}
            onBlur={() => onBlur()}
            onFocus={(event) =>
              setDropdownWidth(event.currentTarget?.clientWidth || 250)
            }
            renderInput={({
              InputProps,
              InputLabelProps,
              inputProps,
              ...params
            }) => (
              <OutlinedInput
                {...params}
                placeholder={placeholder}
                inputRef={InputProps.ref}
                onChange={onInputChange}
                onBlur={onBlurChange}
                sx={{
                  ...inputStyle,
                  ...(fontSize
                    ? {
                        fontSize: fontSizeValue,
                        height: fontSize * 3,
                        minHeight: fontSize * 3,
                      }
                    : {}),
                  ...(disabled
                    ? {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                      }
                    : {}),
                }}
                endAdornment={
                  optional && (
                    <InputAdornment position="end">
                      <Typography variant="subtitle1">Optional</Typography>
                    </InputAdornment>
                  )
                }
                inputProps={{
                  ...merge(inputProps, inputPropsFromProps),
                  "aria-labelledby": `${id}-label`,
                  "data-testid": "autocomplete-input",
                }}
              />
            )}
            blurOnSelect
            disabled={disabled}
            {...autocompleteProps}
            onChange={(e, v, r, d) => {
              if (autocompleteProps.onChange) {
                autocompleteProps.onChange(e, v, r, d);
              }
              onChange(v);
            }}
          />
          <FormHelperText
            variant="outlined"
            {...(!fieldState.error?.message && { sx: { margin: 0 } })}>
            {fieldState.error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};
export default FormAutocomplete;
